import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Icon,
  Paper,
  Chip,
  IconButton,
  Grid,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { purchaseRequestService } from "../../services/purchaseRequest.service";
import { inventoryService } from "../../services/inventory.service";

interface PurchaseRequestItem {
  item_id: string;
  item_name: string;
  uom: string;
  quantity: number;
  reason: string;
  urgency: "Low" | "Medium" | "High";
}

interface PurchaseRequest {
  _id?: string;
  items: PurchaseRequestItem[];
  status?: string;
  created_at?: Date;
  created_by?: string;
}

interface PurchaseRequestFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PurchaseRequest) => void;
  purchaseRequest: any | null;
  loading?: boolean;
}

const initialFormState: PurchaseRequest = {
  items: [],
};

const initialItemState: PurchaseRequestItem = {
  item_id: "",
  item_name: "",
  uom: "",
  quantity: 0,
  reason: "",
  urgency: "Medium",
};

export const PurchaseRequestFormDialog: React.FC<
  PurchaseRequestFormDialogProps
> = ({ open, onClose, onSave, purchaseRequest, loading = false }) => {
  const [formData, setFormData] = useState<PurchaseRequest>(initialFormState);
  const [errors, setErrors] = useState<{
    items?: string;
    [key: string]: string | undefined;
  }>({});
  const [ingredientList, setIngredientList] = useState<
    { label: string; value: string; uom: string }[]
  >([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    if (open) {
      loadIngredients();
    }
  }, [open]);

  useEffect(() => {
    if (purchaseRequest) {
      setFormData({
        ...purchaseRequest,
        items: purchaseRequest.items || [],
      });
    } else {
      setFormData({
        ...initialFormState,
        items: [{ ...initialItemState }], // Start with one empty item
      });
    }
    setErrors({});
  }, [purchaseRequest, open]);

  const loadIngredients = async () => {
    setLoadingItems(true);
    try {
      const response = await inventoryService.listIngredients({
        pageSize: 100,
      });
      const ingredientOptions = response.data.map((ingredient: any) => ({
        label: ingredient.name,
        value: ingredient._id,
        uom: ingredient.unit,
      }));
      setIngredientList(ingredientOptions);
    } catch (error) {
      console.error("Error loading ingredients:", error);
      setIngredientList([]); // Optionally set to empty or fallback
    } finally {
      setLoadingItems(false);
    }
  };

  const handleItemChange = (
    index: number,
    field: keyof PurchaseRequestItem,
    value: string | number
  ) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    // Auto-fill UOM when item is selected
    if (field === "item_id") {
      const selectedItem = ingredientList.find((item) => item.value === value);
      if (selectedItem) {
        newItems[index].item_name = selectedItem.label;
        newItems[index].uom = selectedItem.uom;
      }
    }

    setFormData((prev) => ({
      ...prev,
      items: newItems,
    }));

    setErrors((prev) => ({
      ...prev,
      items: "",
      [`item_${index}_${field}`]: "",
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...initialItemState }],
    }));
    setErrors((prev) => ({
      ...prev,
      items: "",
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Check if at least one item is present
    if (!formData.items || formData.items.length === 0) {
      newErrors.items = "At least one item must be added";
      setErrors(newErrors);
      return false;
    }

    // Validate each item
    let hasValidItems = false;
    formData.items.forEach((item, index) => {
      if (!item.item_id) {
        newErrors[`item_${index}_item_id`] = "Item is required";
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
      }
      if (!item.reason || item.reason.length < 10) {
        newErrors[`item_${index}_reason`] =
          "Reason must be at least 10 characters";
      }
      if (item.reason && item.reason.length > 300) {
        newErrors[`item_${index}_reason`] =
          "Reason must not exceed 300 characters";
      }
      if (!item.urgency) {
        newErrors[`item_${index}_urgency`] = "Urgency is required";
      }

      // Check if this item has all required fields filled
      if (item.item_id && item.quantity > 0 && item.reason && item.urgency) {
        hasValidItems = true;
      }
    });

    if (!hasValidItems) {
      newErrors.items = "At least one complete item must be entered";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      // Filter out incomplete items before saving
      const validItems = formData.items.filter(
        (item) =>
          item.item_id && item.quantity > 0 && item.reason && item.urgency
      );

      onSave({
        ...formData,
        items: validItems,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          minHeight: "80vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              bgcolor: "primary.main",
              borderRadius: "12px",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white" }}>shopping_cart</Icon>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {purchaseRequest ? "Update" : "Create New"} Purchase Request
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2, minHeight: 400 }}>
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon color="action">shopping_cart</Icon>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Request Items
              </Typography>
              <Chip
                label={`${formData.items?.length || 0} item${
                  (formData.items?.length || 0) !== 1 ? "s" : ""
                }`}
                size="small"
                color={
                  (formData.items?.length || 0) > 0 ? "primary" : "default"
                }
              />
            </Box>
            <Button
              startIcon={<Icon>add</Icon>}
              onClick={addItem}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              disabled={loadingItems}
            >
              Add Item
            </Button>
          </Box>

          {errors.items && (
            <Typography
              color="error"
              variant="caption"
              sx={{ mb: 2, display: "block" }}
            >
              {errors.items}
            </Typography>
          )}

          {!formData.items || formData.items.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: "grey.50",
                border: "1px dashed",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography color="textSecondary">
                No items added yet. Click the button above to add items to your
                purchase request.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {formData.items.map((item, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Item #{index + 1}
                    </Typography>
                    {formData.items.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeItem(index)}
                        sx={{ color: "error.main" }}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={ingredientList}
                        value={
                          ingredientList.find(
                            (option) => option.value === item.item_id
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          handleItemChange(
                            index,
                            "item_id",
                            newValue?.value || ""
                          );
                        }}
                        loading={loadingItems}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Item"
                            required
                            error={!!errors[`item_${index}_item_id`]}
                            helperText={
                              errors[`item_${index}_item_id`] ||
                              "Select an item from the master list"
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon fontSize="small">inventory</Icon>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        noOptionsText={
                          loadingItems ? "Loading items..." : "No items found"
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="UOM"
                        value={item.uom}
                        disabled
                        size="small"
                        helperText="Auto-filled based on item"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon fontSize="small">straighten</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={item.quantity || ""}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "quantity",
                            Math.max(0, parseFloat(e.target.value) || 0)
                          )
                        }
                        error={!!errors[`item_${index}_quantity`]}
                        helperText={errors[`item_${index}_quantity`]}
                        required
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon fontSize="small">numbers</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Reason for Request"
                        value={item.reason}
                        onChange={(e) =>
                          handleItemChange(index, "reason", e.target.value)
                        }
                        error={!!errors[`item_${index}_reason`]}
                        helperText={
                          errors[`item_${index}_reason`] ||
                          `${item.reason?.length || 0}/300 characters (min: 10)`
                        }
                        required
                        multiline
                        rows={2}
                        size="small"
                        inputProps={{ maxLength: 300 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon fontSize="small">comment</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={!!errors[`item_${index}_urgency`]}
                      >
                        <InputLabel>Urgency</InputLabel>
                        <Select
                          value={item.urgency}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "urgency",
                              e.target.value as "Low" | "Medium" | "High"
                            )
                          }
                          label="Urgency"
                          startAdornment={
                            <InputAdornment position="start">
                              <Icon fontSize="small">priority_high</Icon>
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="Low">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Icon sx={{ color: "#059669", fontSize: 16 }}>
                                low_priority
                              </Icon>
                              Low
                            </Box>
                          </MenuItem>
                          <MenuItem value="Medium">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Icon sx={{ color: "#F59E0B", fontSize: 16 }}>
                                remove
                              </Icon>
                              Medium
                            </Box>
                          </MenuItem>
                          <MenuItem value="High">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Icon sx={{ color: "#DC2626", fontSize: 16 }}>
                                priority_high
                              </Icon>
                              High
                            </Box>
                          </MenuItem>
                        </Select>
                        {errors[`item_${index}_urgency`] && (
                          <Typography variant="caption" color="error">
                            {errors[`item_${index}_urgency`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2, px: 3 }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={loading}
          loadingPosition="start"
          startIcon={<Icon>save</Icon>}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {purchaseRequest ? "Update" : "Create"} Purchase Request
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
